import SearchIcon from "@mui/icons-material/Search";
import { Grid, Typography } from "@mui/material";
import { differenceInHours, formatDuration, intervalToDuration } from "date-fns";
import { useCallback } from "react";
import { FormDataConsumer, SaveButton, SimpleForm } from "react-admin";
import CustomDateTimeInput from "shared/components/inputs/CustomDateTimeInput";
import { DateRangeType } from "shared/types";
import { NullableDateRange } from "./hooks/useDateRangePicker";

export type DateRangePickerProps = {
  setDateRange: (values: NullableDateRange) => void;
  dateRange: DateRangeType;
  limitRangeInHours?: number;
  enforceMinMax?: boolean;
};

const DateRangePicker = ({
  setDateRange,
  dateRange,
  limitRangeInHours = 168,
  enforceMinMax = true,
}: DateRangePickerProps) => {
  function getErrorMessage(hours: number): string {
    const duration = intervalToDuration({ start: 0, end: hours * 60 * 60 * 1000 });
    const formattedDuration = formatDuration(duration, { format: ["weeks", "days", "hours"] });

    return `Date range should be less than ${formattedDuration}`;
  }

  const dateWarning = limitRangeInHours ? getErrorMessage(limitRangeInHours) : "";

  const validateForm = useCallback(
    ({ startDate, endDate }: NullableDateRange) => {
      const errors: NullableDateRange = {
        startDate: "",
        endDate: "",
      };
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (start > end) {
          errors.startDate += "Start date should be before end date";
          errors.endDate += "End date should be after start date";
        }
        if (differenceInHours(end, start) > limitRangeInHours) {
          errors.startDate += getErrorMessage(limitRangeInHours);
          errors.endDate += getErrorMessage(limitRangeInHours);
        }
      }
      return {
        ...(errors.startDate ? { startDate: errors.startDate } : {}),
        ...(errors.endDate ? { endDate: errors.endDate } : {}),
      };
    },
    [limitRangeInHours],
  );

  return (
    <SimpleForm
      onSubmit={setDateRange}
      shouldUnregister
      sanitizeEmptyValues
      toolbar={false}
      mode="onBlur"
      reValidateMode="onBlur"
      validate={validateForm}
    >
      <Grid container justifyContent={"center"} alignItems={"center"} gap={2}>
        <Grid item>
          <Typography variant="h6" sx={{ mb: "20px" }}>
            Date Range :
          </Typography>
        </Grid>

        <Grid item>
          <FormDataConsumer>
            {({ formData }) => {
              return (
                <CustomDateTimeInput
                  source="startDate"
                  defaultValue={dateRange.startDate}
                  helperText={dateWarning}
                  maxDate={formData.endDate && enforceMinMax ? formData.endDate : null}
                />
              );
            }}
          </FormDataConsumer>
        </Grid>
        <Grid item>
          <FormDataConsumer>
            {({ formData }) => {
              return (
                <CustomDateTimeInput
                  source="endDate"
                  defaultValue={dateRange.endDate}
                  helperText={dateWarning}
                  minDate={formData.startDate && enforceMinMax ? formData.startDate : null}
                />
              );
            }}
          </FormDataConsumer>
        </Grid>
        <Grid item>
          <SaveButton sx={{ mb: "20px" }} label="Search" icon={<SearchIcon />} />
        </Grid>
      </Grid>
    </SimpleForm>
  );
};

export default DateRangePicker;
